import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { Document, Page, pdfjs } from "react-pdf";
import {
  getDocumentForSign,
  submitSignedDocument,
} from "../../api/documentsApi";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DocumentSignPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("stoken");

  const sigCanvas = useRef(null);

  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);

  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);

  // Load document
  useEffect(() => {
    const loadDocument = async () => {
      try {
        const data = await getDocumentForSign(id, token);
        setPdfUrl(data.file);
      } catch (err) {
        console.error("Failed to load document", err);
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [id, token]);

  const handleClear = () => {
    sigCanvas.current.clear();
  };

  const handleSign = async () => {
    if (sigCanvas.current.isEmpty()) {
      alert("Please draw your signature");
      return;
    }

    try {
      setSigning(true);

      const signatureBase64 = sigCanvas.current
        .getCanvas()
        .toDataURL("image/png");

      const payload = {
        signature: signatureBase64,
      };

      await submitSignedDocument(id, payload, token);

      alert("Document signed successfully!");
    } catch (err) {
      console.error(err);
      alert("Signing failed");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center text-white">
        Loading document...
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-slate-950">
      {/* PDF Viewer */}
      <div className="flex-1 overflow-auto p-6">
        {pdfUrl && (
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <Page
                key={index}
                pageNumber={index + 1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            ))}
          </Document>
        )}
      </div>

      {/* Signature Panel */}
      <div className="w-[380px] bg-slate-900 border-l border-white/10 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Signature</h2>

        <p className="text-sm text-slate-400 mb-4">
          Draw your signature with mouse or touchpad
        </p>

        <div className="bg-white rounded-md overflow-hidden">
          <SignatureCanvas
            ref={sigCanvas}
            penColor="black"
            canvasProps={{
              className: "w-full h-48",
            }}
          />
        </div>

        <button
          onClick={handleClear}
          className="text-sm text-red-400 mt-2 hover:text-red-500"
        >
          Clear
        </button>

        <button
          onClick={handleSign}
          disabled={signing}
          className="w-full mt-6 bg-pink-600 hover:bg-pink-700 text-white py-3 rounded-lg font-medium transition"
        >
          {signing ? "Signing..." : "Sign Document"}
        </button>
      </div>
    </div>
  );
};

export default DocumentSignPage;
